import { SnackbarProvider } from 'notistack'
import { usePostHog } from 'posthog-js/react'
import { useEffect, useState } from 'react'
import { ScrollRestoration } from 'react-router-dom'
import { RxDatabase, RxState } from 'rxdb'
import { Provider } from 'rxdb-hooks'

import { AuthStateContext } from '@/modules/auth/context.ts'
import { Layout } from '@/modules/layouts/Layout.tsx'
import { NetworkStateContext } from '@/modules/network/context.ts'
import {
  ApplicationAuthState,
  ApplicationNetworkState,
} from '@/system/rxdb/replication.ts'
import { createDb } from '@/system/rxdb/setup.js'

import ServiceWorker from './ServiceWorker.tsx'

function App() {
  const [db, setDb] = useState<RxDatabase | Error>()
  const [authState, setAuthState] = useState<RxState<ApplicationAuthState>>()
  const [networkState, setNetworkState] =
    useState<RxState<ApplicationNetworkState>>()
  const posthog = usePostHog()
  useEffect(() => {
    const handleError = (e: Error) => {
      console.error('Unable to initialize database', e)
      setDb(e)
    }
    createDb(posthog)
      .then(function self({
        db,
        state,
        networkState,
        registerOnDeleteHandler,
      }) {
        setDb(db)
        setAuthState(state)
        setNetworkState(networkState)
        registerOnDeleteHandler((db) => {
          // in case we are logged out we reset the state and database
          db.then(self).catch(handleError)
        })
      })
      .catch(handleError)
  }, [posthog])

  // Forward any errors to route error handler
  if (db instanceof Error) {
    throw db
  }

  return (
    <Provider db={db}>
      <AuthStateContext.Provider value={authState || null}>
        <NetworkStateContext.Provider value={networkState || null}>
          <SnackbarProvider maxSnack={3}>
            <ScrollRestoration />
            <Layout />
            <ServiceWorker />
          </SnackbarProvider>
        </NetworkStateContext.Provider>
      </AuthStateContext.Provider>
    </Provider>
  )
}

export default App
